import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Output,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { startWith, take } from 'rxjs/operators';

import { User } from '../shared/models/user';
import { AuthService } from '../shared/services/auth.service';
import { UserService } from '../shared/services/user.service';
import { LicenseInfo, License, Card, Subscription } from '../shared/models/license-info';
import { AccountPaymentMethodComponent } from '../account/account-payment-method/account-payment-method.component';
import { PaymentService } from '../payment/payment.service';

@Component({
  selector: 'app-header',
  templateUrl: 'header.component.html',
  styleUrls: ['header.component.css'],
  imports: [DatePipe, AccountPaymentMethodComponent],
  providers: [PaymentService],
  standalone: true,
})
export class HeaderComponent {
  @Output() loginClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() logoutClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

  private readonly _userService = inject(UserService);
  private readonly _authService = inject(AuthService);
  private readonly _elementRef = inject(ElementRef);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _user = toSignal(this._userService.user$, { initialValue: null as User | null });
  private readonly _isLoggedIn = toSignal(
    this._authService.isLoggedIn$.pipe(startWith(this._authService.isLoggedIn())),
    { initialValue: this._authService.isLoggedIn() }
  );
  private readonly _isUserMenuOpen = signal(false);
  private readonly _isPaymentMethodModalOpen = signal(false);
  private readonly _licenseInfo = signal<LicenseInfo | null>(null);
  private readonly _isLoadingLicenseInfo = signal(false);
  private readonly _licenseLoaded = signal(false);

  private readonly _displayName = computed(() => {
    this._isLoggedIn();
    const user = this._user();
    const firstName = this._authService.firstName || user?.FirstName;
    const lastName = this._authService.lastName || user?.LastName;
    return [firstName, lastName].filter(Boolean).join(' ').trim();
  });

  private readonly _displayEmail = computed(() => {
    this._isLoggedIn();
    const user = this._user();
    return user?.Email || this._authService.userEmail || this.getTokenClaim('email') || '';
  });

  private readonly _displaySchool = computed(() => {
    this._isLoggedIn();
    const school = this.userSchool(this._user());
    return school || this.getTokenClaim('school') || this.getTokenClaim('district') || '';
  });

  private readonly _initials = computed(() => {
    this._isLoggedIn();
    const user = this._user();
    const firstName = this._authService.firstName || user?.FirstName;
    const lastName = this._authService.lastName || user?.LastName;
    return this.getInitials(firstName, lastName, this._displayName());
  });

  private readonly _activeLicense = computed(() => this._licenseInfo()?.Licenses?.[0] || null);
  private readonly _activeCard = computed(() => this._licenseInfo()?.Cards?.[0] || null);
  private readonly _activeCardSubscriptions = computed(() => {
    const card = this._activeCard();
    if (!card) {
      return [] as Subscription[];
    }

    if (Array.isArray(card.Subscriptions) && card.Subscriptions.length > 0) {
      return card.Subscriptions;
    }

    if (card.Subscription) {
      return [card.Subscription];
    }

    return [] as Subscription[];
  });

  constructor() {
    effect(() => {
      const isLoggedIn = this._isLoggedIn();

      if (!isLoggedIn) {
        this._licenseInfo.set(null);
        this._licenseLoaded.set(false);
        this._isLoadingLicenseInfo.set(false);
        return;
      }

      if (this._licenseLoaded()) {
        return;
      }

      this.loadLicenseInfo();
    });
  }

  private loadLicenseInfo(): void {
    this._isLoadingLicenseInfo.set(true);
    this._userService
      .getLicenseInfo()
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: data => {
          this._licenseInfo.set(data);
          this._licenseLoaded.set(true);
          this._isLoadingLicenseInfo.set(false);
        },
        error: () => {
          this._licenseInfo.set(null);
          this._licenseLoaded.set(true);
          this._isLoadingLicenseInfo.set(false);
        },
      });
  }

  get displayName(): string {
    return this._displayName();
  }

  get displaySchool(): string {
    return this._displaySchool();
  }

  get displayEmail(): string {
    return this._displayEmail();
  }

  get initials(): string {
    return this._initials();
  }

  get isUserMenuOpen(): boolean {
    return this._isUserMenuOpen();
  }

  get licenseInfo(): LicenseInfo | null {
    return this._licenseInfo();
  }

  get isPaymentMethodModalOpen(): boolean {
    return this._isPaymentMethodModalOpen();
  }

  get isLoadingLicenseInfo(): boolean {
    return this._isLoadingLicenseInfo();
  }

  get activeLicense(): License | null {
    return this._activeLicense();
  }

  get activeCard(): Card | null {
    return this._activeCard();
  }

  get activeCardSubscriptions(): Subscription[] {
    return this._activeCardSubscriptions();
  }

  toggleUserMenu(event: MouseEvent) {
    event.stopPropagation();
    this._isUserMenuOpen.update(isOpen => !isOpen);
  }

  closeUserMenu() {
    this._isUserMenuOpen.set(false);
  }

  openPaymentMethodModal(event: MouseEvent) {
    event.stopPropagation();
    this._isPaymentMethodModalOpen.set(true);
    this.closeUserMenu();
  }

  closePaymentMethodModal() {
    this._isPaymentMethodModalOpen.set(false);
  }

  onPaymentMethodSaved() {
    this._isPaymentMethodModalOpen.set(false);
    this._licenseLoaded.set(false);
    this.loadLicenseInfo();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.isUserMenuOpen) {
      return;
    }

    const clickedInside = this._elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.closeUserMenu();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePress() {
    if (this.isPaymentMethodModalOpen) {
      this.closePaymentMethodModal();
      return;
    }

    this.closeUserMenu();
  }

  onSignOut() {
    this.closeUserMenu();
    this.logoutClicked.emit(true);
  }

  private userSchool(user: User | null): string {
    if (!user) {
      return '';
    }

    const userData = user as any;
    return (
      userData.School ||
      userData.SchoolName ||
      userData.District ||
      userData.DistrictName ||
      userData.CustomerName ||
      ''
    );
  }

  private getTokenClaim(claimName: string): string {
    const decodedToken = this._authService.decodedToken;
    if (!decodedToken) {
      return '';
    }

    return (
      decodedToken[claimName] ||
      decodedToken[claimName.toLowerCase()] ||
      decodedToken[claimName.toUpperCase()] ||
      ''
    );
  }

  private getInitials(firstName: string, lastName: string, fullName: string): string {
    const firstInitial = (firstName || '').trim().charAt(0);
    const lastInitial = (lastName || '').trim().charAt(0);
    const fromNames = `${firstInitial}${lastInitial}`.toUpperCase();

    if (fromNames.trim()) {
      return fromNames;
    }

    const parts = (fullName || '').trim().split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }

    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }

    return 'U';
  }
}
